import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import { determinePublicationStatus } from '@wilson/activities/pipes';
import { AuthState } from '@wilson/auth/core';
import {
  Activity,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  OperationStatus,
  PublicationStatus,
  Shift,
  TimelineShift,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import { ShiftQuickCheckService } from '@wilson/non-domain-specific/shift-helpers/core';
import { ShiftTimelinePreferredSettingsState } from '@wilson/preferred-settings/state';
import {
  AddShiftWithoutActivitiesToCellViewUnassignedRegion,
  AddShiftWithoutActivitiesToUnassignedRegion,
  BulkDeleteShiftActivitiesFromUnassignedRegion,
  RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion,
  RemoveShiftWithoutActivitiesFromUnassignedRegion,
  ShiftTimelineDataState,
  ShiftTimelineDataTransformerService,
  ShiftTimelineSettingsState,
  ShiftTimelineUnassignedRegionState,
  TimelineAssignedShiftValidationsStateService,
  TimelineRemoveActivityFromShiftV2,
  TimelineUnassignedShiftValidationsStateService,
  TimelineUpdateAssignmentsV2,
  TimelineUpdateShiftV2,
  TimelineUpsertActivityToUnassignedRegion,
  TimelineUpsertActivityV2,
  UpdateKpisOfUsers,
  UpdateProductivityOfUsers,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { firstValueFrom } from 'rxjs';
import { SimplifiedDrawerShift } from '../../types';
import { ReloadedShift } from './simplified-shift-save.types';

const RELOAD_SHIFT_RELATIONS = [
  'user',
  'user.contract',
  'user.contract.activityCategoryContracts',
  'shiftSeries',
  'shiftCategory',
  'organizationalUnit',
  'activities',
  'activities.agreement',
  'activities.profession',
  'activities.agreement.client',
  'activities.endLocation',
  'activities.startLocation',
  'activities.activityCategory',
  'activities.activityReports',
  'activities.serviceDeviations',
  'activities.service',
  'labels',
  'payrollInformation',
  'startLocation',
  'endLocation',
];

/**
 * Fetches/reloads the drawer shift and syncs timeline state (activities,
 * validations, KPIs). No form mutation — callers own form re-init.
 */
@Injectable()
export class SimplifiedShiftReloadAndTimelineSyncService {
  private readonly shiftsService = inject(ShiftsService);
  private readonly store = inject(Store);
  private readonly shiftTimelineDataTransformerService = inject(
    ShiftTimelineDataTransformerService,
  );
  private readonly timelineAssignedShiftValidationsStateService = inject(
    TimelineAssignedShiftValidationsStateService,
  );
  private readonly timelineUnassignedShiftValidationsStateService = inject(
    TimelineUnassignedShiftValidationsStateService,
  );
  private readonly shiftQuickCheckService = inject(ShiftQuickCheckService);

  async reloadShiftAndUpdateTimeline(
    shiftId: string,
    options?: {
      updatePublicationStatus?: boolean;
      shiftFieldsToUpdate?: { startDate?: string };
    },
  ): Promise<ReloadedShift | null> {
    const reloadedShift =
      options?.updatePublicationStatus || options?.shiftFieldsToUpdate
        ? await this.fetchShiftAndApplyUpdates(shiftId, options)
        : await this.fetchReloadedShift(shiftId);

    if (!reloadedShift) {
      return null;
    }

    await this.updateTimelineFromDrawer(reloadedShift);

    if (reloadedShift.userId) {
      await firstValueFrom(
        this.updateUsersAssignmentsState([reloadedShift.userId]),
      );
      this.updateProductivityAndKpisOfUsers([reloadedShift.userId]);
    }

    return reloadedShift;
  }

  isCellView(): boolean {
    const currentUserId = this.store.selectSnapshot(AuthState.userId);
    return currentUserId
      ? this.store.selectSnapshot(
          ShiftTimelinePreferredSettingsState.isCellView(currentUserId),
        )
      : false;
  }

  updateUsersAssignmentsState(userIds: string[]) {
    const visibleDateRange = this.store.selectSnapshot(
      ShiftTimelineDataState.visibleDateRange,
    );
    return this.store.dispatch(
      new TimelineUpdateAssignmentsV2({
        userIds,
        dates: visibleDateRange,
      }),
    );
  }

  updateProductivityAndKpisOfUsers(userIds: string[]): void {
    if (this.isProductivityVisible()) {
      this.store.dispatch(new UpdateProductivityOfUsers(userIds));
    }
    if (this.isUserKpisVisible()) {
      this.store.dispatch(new UpdateKpisOfUsers(userIds));
    }
  }

  getTimelineShiftForAssignment(
    currentShift: SimplifiedDrawerShift,
    previousUserId: string | null,
  ): TimelineShift {
    if (previousUserId) {
      const existingAssignedShift = this.store.selectSnapshot(
        ShiftTimelineDataState.getShiftWithoutActivities(currentShift.id),
      );
      if (existingAssignedShift) {
        return existingAssignedShift;
      }
    } else {
      const isCellView = this.isCellView();
      const existingUnassignedShift = isCellView
        ? this.store.selectSnapshot(
            ShiftTimelineUnassignedRegionState.getCellViewShiftWithoutActivities(
              currentShift.id,
            ),
          )
        : this.store.selectSnapshot(
            ShiftTimelineUnassignedRegionState.getShiftWithoutActivities(
              currentShift.id,
            ),
          );
      if (existingUnassignedShift) {
        return {
          ...existingUnassignedShift,
          isHighlighted: false,
        };
      }
    }

    return this.shiftTimelineDataTransformerService.convertShiftToShiftWithoutActivities(
      currentShift as Shift &
        WithPreparedAttributes & { activities: Activity[]; id: string },
    ) as TimelineShift;
  }

  migrateUnassignedActivitiesIfNeeded(
    shiftId: string,
    isCellView: boolean,
  ): void {
    if (isCellView) {
      return;
    }

    const areActivitiesVisible = this.store.selectSnapshot(
      ShiftTimelineSettingsState.areActivitiesVisible,
    );
    if (!areActivitiesVisible) {
      return;
    }

    const shiftActivities = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.unassignedShiftActivitiesArray(
        shiftId,
      ),
    );
    if (shiftActivities.length === 0) {
      return;
    }

    this.store.dispatch([
      new TimelineUpsertActivityV2(shiftActivities, shiftId),
      new BulkDeleteShiftActivitiesFromUnassignedRegion(shiftId),
    ]);
  }

  removeShiftFromUnassignedRegion(shiftId: string, isCellView: boolean): void {
    if (isCellView) {
      this.store.dispatch([
        new RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion({
          shiftId,
        }),
      ]);
      return;
    }

    this.store.dispatch([
      new RemoveShiftWithoutActivitiesFromUnassignedRegion({ shiftId }),
    ]);
  }

  addShiftToUnassignedRegion(
    shiftToUpdate: SimplifiedDrawerShift,
    isCellView: boolean,
  ): void {
    const convertedShift =
      this.shiftTimelineDataTransformerService.convertShiftToShiftWithoutActivities(
        shiftToUpdate as Shift &
          WithPreparedAttributes & { activities: Activity[]; id: string },
      );

    const transformedShift =
      this.shiftTimelineDataTransformerService.convertToUnassignedShift(
        convertedShift as Shift &
          WithPreparedAttributes & { activities: Activity[]; id: string },
      );

    if (isCellView) {
      this.store.dispatch([
        new AddShiftWithoutActivitiesToCellViewUnassignedRegion(
          transformedShift,
        ),
      ]);
      return;
    }

    this.store.dispatch([
      new AddShiftWithoutActivitiesToUnassignedRegion(transformedShift),
    ]);
  }

  private async fetchReloadedShift(
    shiftId: string,
  ): Promise<ReloadedShift | null> {
    return (await firstValueFrom(
      this.shiftsService.fetchShiftWithTranslatedActivityCategoryAndSortActivities(
        shiftId,
        { relations: RELOAD_SHIFT_RELATIONS },
      ),
    )) as ReloadedShift | null;
  }

  private async fetchShiftAndApplyUpdates(
    shiftId: string,
    options: {
      updatePublicationStatus?: boolean;
      shiftFieldsToUpdate?: { startDate?: string };
    },
  ): Promise<ReloadedShift | null> {
    const shift = await this.fetchReloadedShift(shiftId);
    if (!shift) {
      return null;
    }

    const patch: {
      id: string;
      startDate?: string;
      publicationStatus?: PublicationStatus;
    } = { id: shiftId };
    let hasUpdates = false;

    const nextStartDate = options.shiftFieldsToUpdate?.startDate;
    if (nextStartDate && nextStartDate !== shift.startDate) {
      patch.startDate = nextStartDate;
      hasUpdates = true;
    }

    if (options.updatePublicationStatus) {
      const publicationStatus = determinePublicationStatus(
        shift.activities,
        shift.publicationStatus,
      );
      if (publicationStatus !== shift.publicationStatus) {
        patch.publicationStatus = publicationStatus;
        hasUpdates = true;
      }
    }

    if (!hasUpdates) {
      return shift;
    }

    await firstValueFrom(this.shiftsService.updateShift(patch));

    return {
      ...shift,
      ...(patch.startDate ? { startDate: patch.startDate } : {}),
      ...(patch.publicationStatus
        ? { publicationStatus: patch.publicationStatus }
        : {}),
    };
  }

  private async updateTimelineFromDrawer(
    reloadedShift: ReloadedShift,
  ): Promise<void> {
    const isUnassignedShift = !reloadedShift.userId;

    this.syncTimelineShiftAndActivities(reloadedShift, isUnassignedShift);

    const validationService = isUnassignedShift
      ? this.timelineUnassignedShiftValidationsStateService
      : this.timelineAssignedShiftValidationsStateService;

    validationService.queueValidationRequest([reloadedShift.id], {
      forceRefresh: true,
    });

    try {
      await this.shiftQuickCheckService.validateShiftForQuickCheck(
        reloadedShift.id,
        reloadedShift.userId as string,
      );
    } catch (error) {
      captureException(error);
    }
  }

  private syncTimelineShiftAndActivities(
    reloadedShift: ReloadedShift,
    isUnassignedShift: boolean,
  ): void {
    const timelineShift =
      this.shiftTimelineDataTransformerService.convertShiftToShiftWithoutActivities(
        reloadedShift as Shift &
          WithPreparedAttributes & { activities: Activity[]; id: string },
      ) as TimelineShift;

    if (isUnassignedShift) {
      this.updateUnassignedRegionShift(reloadedShift);
    } else if (reloadedShift.userId) {
      this.store.dispatch(new TimelineUpdateShiftV2(timelineShift));
    }

    const areActivitiesVisible = this.store.selectSnapshot(
      ShiftTimelineSettingsState.areActivitiesVisible,
    );
    if (!areActivitiesVisible) {
      return;
    }

    const activities = reloadedShift.activities.filter(
      (activity) =>
        activity.operationalStatus !== OperationStatus.SkippedByUser,
    ) as ActivityWithCategoryAndLocationsAndReportedDetails[];

    if (!activities.length) {
      return;
    }

    if (isUnassignedShift) {
      if (this.isCellView()) {
        return;
      }

      this.store.dispatch([
        new BulkDeleteShiftActivitiesFromUnassignedRegion(reloadedShift.id),
      ]);
      activities.forEach((activity) => {
        this.store.dispatch(
          new TimelineUpsertActivityToUnassignedRegion(
            activity,
            reloadedShift.id,
          ),
        );
      });
      return;
    }

    const removeActions = this.getRemoveOrphanedAssignedActivityActions(
      reloadedShift.id,
      activities,
    );

    this.store.dispatch([
      ...removeActions,
      new TimelineUpsertActivityV2(activities, reloadedShift.id),
    ]);
  }

  private getRemoveOrphanedAssignedActivityActions(
    shiftId: string,
    reloadedActivities: ActivityWithCategoryAndLocationsAndReportedDetails[],
  ): TimelineRemoveActivityFromShiftV2[] {
    const activityRows = this.store.selectSnapshot(
      ShiftTimelineDataState.shiftDeterminedActivities(shiftId),
    );
    const existingActivityIds = activityRows.flatMap((row) => Object.keys(row));
    const reloadedActivityIds = new Set(
      reloadedActivities
        .map((activity) => activity.id)
        .filter((id): id is string => !!id),
    );

    return existingActivityIds
      .filter((activityId) => !reloadedActivityIds.has(activityId))
      .map(
        (activityId) =>
          new TimelineRemoveActivityFromShiftV2(activityId, shiftId),
      );
  }

  private updateUnassignedRegionShift(shiftToUpdate: ReloadedShift): void {
    const convertedShift =
      this.shiftTimelineDataTransformerService.convertShiftToShiftWithoutActivities(
        shiftToUpdate as Shift &
          WithPreparedAttributes & { activities: Activity[]; id: string },
      );

    const transformedShift =
      this.shiftTimelineDataTransformerService.convertToUnassignedShift(
        convertedShift as Shift &
          WithPreparedAttributes & { activities: Activity[]; id: string },
      );

    const isCellView = this.isCellView();

    if (isCellView) {
      this.store.dispatch([
        new RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion({
          shiftId: transformedShift.id,
        }),
        new AddShiftWithoutActivitiesToCellViewUnassignedRegion(
          transformedShift,
        ),
      ]);
      return;
    }

    this.store.dispatch([
      new RemoveShiftWithoutActivitiesFromUnassignedRegion({
        shiftId: transformedShift.id,
      }),
      new AddShiftWithoutActivitiesToUnassignedRegion(transformedShift),
    ]);
  }

  private isProductivityVisible(): boolean {
    const userId = this.store.selectSnapshot(AuthState.userId);
    return userId
      ? this.store.selectSnapshot(
          ShiftTimelinePreferredSettingsState.showProductivityKpi(userId),
        )
      : false;
  }

  private isUserKpisVisible(): boolean {
    const userId = this.store.selectSnapshot(AuthState.userId);
    return userId
      ? this.store.selectSnapshot(
          ShiftTimelinePreferredSettingsState.isUserKpisVisible(userId),
        )
      : false;
  }
}
